import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { Search, Plus, Link2 } from "lucide-react";
import useRequest from "../../../hook/use-req";
import { CampaignData } from "../../../types";
import { SingleCampaignResponse } from "../../../../pages/campaign/types";
import { LivePreviewPanel } from "./live-preview";
import ConvoAIPanel from "../convoai-hub";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SmsContentCreationProps {
  campaignData: CampaignData;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
  onBack: () => void;
}

const MOCK_TEMPLATES = [
  { id: 1, name: "Seasonal Promo", preview: "Hello user, 😊 Our sale is here. Get 10% off with code SALE120. Shop today!" },
  { id: 2, name: "Flash Sale Alert", preview: "Hello user, Our sale is here. Get 10% off with code SALE120. Shop today!" },
  { id: 3, name: "Win-back offer", preview: "Hello user, Our sale is here. Get 10% off with code SALE120. Shop today!" },
  { id: 4, name: "New service offer", preview: "Hello user, Our sale is here. Get 10% off with code SALE120. Shop today!" },
  { id: 5, name: "Welcome promo", preview: "Hello user, Our sale is here. Get 10% off with code SALE120. Shop today!" },
  { id: 6, name: "Dussehra Sale Alert", preview: "Hello user, Our sale is here. Get 10% off with code SALE120. Shop today!" },
  { id: 7, name: "Christmas Sale Alert", preview: "Hello user, Our sale is here. Get 10% off with code SALE120. Shop today!" },
];

// ─── Right Panel Component (like in Messaging) ────────────────────────────────

interface RightPanelProps {
  messageText: string;
  senderName?: string;
  isConvoOpen: boolean;
  onToggleConvo: () => void;
  onConvoSend: (message: string, history: Array<{ role: "user" | "ai"; text: string }>) => Promise<string>;
}

function RightPanel({ messageText, senderName, isConvoOpen, onToggleConvo, onConvoSend }: RightPanelProps) {
  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-slate-100 shrink-0">
        <span className="text-sm font-semibold text-slate-700">Live Preview</span>
        <button
          onClick={onToggleConvo}
          title={isConvoOpen ? "Back to preview" : "Open ConvoAI"}
          className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors
            ${isConvoOpen
              ? "bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
              : "text-slate-500 hover:text-slate-700"}`}
        >
          {isConvoOpen ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {/* Live Preview Panel */}
        <div className={`absolute inset-0 flex flex-col items-center justify-start pt-6 pb-4 px-4
          overflow-y-auto transition-opacity duration-200
          ${isConvoOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          <LivePreviewPanel
            isExpanded={true}
            onToggle={() => { }}
            senderName={senderName}
            messageText={messageText}
          />
          <button
            onClick={onToggleConvo}
            className="mt-5 w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg
              hover:bg-indigo-700 transition-colors"
            title="Open ConvoAI"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H9l-4 4V14H4a2 2 0 01-2-2V5z" />
            </svg>
          </button>
        </div>

        {/* ConvoAI Panel */}
        <div className={`absolute inset-0 transition-opacity duration-200
          ${isConvoOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ConvoAIPanel
            isOpen={isConvoOpen}
            onClose={onToggleConvo}
            onSend={onConvoSend}
            placeholder="Ask anything..."
            greeting="I can help you craft the perfect SMS message. Ask me to adjust the tone, add emojis, shorten the text, or anything else!"
            quickActions={["Adjust the Tone", "Change promo", "Edit URL", "Add Emojis", "Make it shorter", "Make it more formal"]}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export const SmsContentCreation: React.FC<SmsContentCreationProps> = ({
  campaignData,
  singleCampaign,
  setCampaignData,
  onSubmit,
  onValidation,
  onBack,
}) => {
  const token = localStorage.getItem("authToken");
  const [messageText, setMessageText] = useState<string>(campaignData?.messages || "");
  const [extractedUrl, setExtractedUrl] = useState<string>("");
  const [shortenedUrl, setShortenedUrl] = useState<string>("");
  const [enableLinkShortening, setEnableLinkShortening] = useState<boolean>(false);

  const [isLinkValid, setIsLinkValid] = useState(true);
  const [isMessageTextValid, setIsMessageTextValid] = useState(!!campaignData?.messages);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [isConvoOpen, setIsConvoOpen] = useState(false);
  const [convoHistory, setConvoHistory] = useState<Array<{ role: "user" | "ai"; text: string }>>([]);

  const { makeRequest: shortLinkRequest, loading } = useRequest(
    `/campaign/track?url_link=${encodeURIComponent(extractedUrl)}`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues: { ...campaignData, messages: campaignData?.messages || "" },
    mode: "onChange",
  });

  const watchedValues = watch();

  // Helpers
  const extractUrlFromText = (text: string): string => {
    const urls = text.match(/(https?:\/\/[^\s]+)/g);
    return urls?.[0] ?? "";
  };

  const removeUrlFromText = (text: string): string =>
    text.replace(/(https?:\/\/[^\s]+)/g, "").trim();

  const removeEmojis = (text: string) =>
    text.replace(/([\u2600-\u27BF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g, "");

  const isValidUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      const u = new URL(url);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  const calculateSMSSegments = (text: string) => {
    const textWithoutUrl = removeUrlFromText(text);
    const urlLen = enableLinkShortening && shortenedUrl
      ? shortenedUrl.length
      : extractedUrl.length;
    const totalChars = textWithoutUrl.length + urlLen;
    const segments = Math.ceil(totalChars / 160) || 1;
    return {
      segments,
      availableChars: segments * 160 - totalChars,
      textOnlyCount: textWithoutUrl.length,
      totalChars,
    };
  };

  const { segments, totalChars } = calculateSMSSegments(messageText);
  const smsMessageUnit = Math.max(segments, campaignData.smsSegment || 1, 1);

  // Sync
  useEffect(() => {
    setCampaignData((prev) => ({ ...prev, ...watchedValues, smsSegment: prev.smsSegment || 1 }));
  }, [watchedValues, setCampaignData]);

  useEffect(() => {
    const { segments: s } = calculateSMSSegments(messageText);
    setCampaignData((prev) => ({
      ...prev,
      messages: messageText,
      link: enableLinkShortening && shortenedUrl ? shortenedUrl : extractedUrl,
      smsSegment: Math.max(s, 1),
    }));
  }, [messageText, extractedUrl, shortenedUrl, enableLinkShortening]);

  useEffect(() => {
    setIsMessageTextValid(messageText.length > 0);
  }, [messageText]);

  useEffect(() => {
    setIsLinkValid(extractedUrl ? isValidUrl(extractedUrl) : true);
    if (enableLinkShortening && extractedUrl && !shortenedUrl) handleShortenUrl(extractedUrl);
  }, [extractedUrl, enableLinkShortening]);

  useEffect(() => {
    const url = extractUrlFromText(messageText);
    setExtractedUrl(url);
  }, [messageText]);

  useEffect(() => {
    onValidation(isValid && isMessageTextValid && isLinkValid);
  }, [isValid, isMessageTextValid, isLinkValid, onValidation]);

  useEffect(() => {
    if (singleCampaign?.content?.message) {
      const msg = singleCampaign.content.message;
      reset({ ...getValues(), messages: msg });
      setMessageText(msg);
    }
  }, [singleCampaign]);

  const handleShortenUrl = async (url?: string) => {
    const urlToShorten = url || extractedUrl;
    if (!urlToShorten) return;

    const [response] = await shortLinkRequest();
    if (response?.status) {
      setShortenedUrl(response.shortened_url);
      toast.success(response.message || "Link shortened successfully!");
    } else {
      toast.error(response.detail || "Failed to generate!");
    }
  };

  const handleToggleShortening = async (enabled: boolean) => {
    setEnableLinkShortening(enabled);
    if (enabled && extractedUrl && !shortenedUrl) await handleShortenUrl();
  };

  const handleSelectTemplate = (template: (typeof MOCK_TEMPLATES)[0]) => {
    setSelectedTemplateId(template.id);
    const sanitized = removeEmojis(template.preview);
    setMessageText(sanitized);
    setValue("messages", sanitized, { shouldValidate: true });
  };

  const handleFormSubmit = (data: any) => {
    if (!data.messages?.trim()) { toast.error("Message field is required"); return; }
    if (extractedUrl && !isValidUrl(extractedUrl)) { toast.error("Please enter a valid URL"); return; }
    onSubmit({
      messages: data.messages,
      link: enableLinkShortening && shortenedUrl ? shortenedUrl : extractedUrl,
      smsSegment: campaignData.smsSegment,
    });
  };

  const handleConvoSend = async (message: string, history: Array<{ role: "user" | "ai"; text: string }>): Promise<string> => {
    setConvoHistory(history);

    // Simulate AI delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const lowerMessage = message.toLowerCase();
    let newMessageText = "";
    let response = "";

    // Check for different commands
    if (lowerMessage.includes("adjust the tone")) {
      newMessageText = "Hey there! 🎉 Great news! Our amazing sale is here. Grab 10% off with code SALE120. Shop now!";
      response = `I've adjusted the tone. Here's your updated message:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("change promo")) {
      newMessageText = "Limited time offer! 🚀 Get 20% off your next purchase with code FLASH20. Don't miss out!";
      response = `I've updated the promo. New message:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("add emoji")) {
      newMessageText = "Hello user! 😊 Our sale is here! 🎉 Get 10% off with code SALE120 💰 Shop today! 🛍️";
      response = `I've added some emojis:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("make it shorter")) {
      newMessageText = "Sale alert! 10% off with SALE120. Shop now!";
      response = `Here's a shorter version:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("make it more formal")) {
      newMessageText = "Dear valued customer, enjoy 10% savings using code SALE120. Visit our store today.";
      response = `Here's a more formal version:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("edit url")) {
      response = "I've extracted the URL from your message. You can edit it in the Destination URL field above.";
    }
    else {
      response = "I've processed your request. Let me know if you need any other adjustments!";
    }

    // Update the message text if we have a new message
    if (newMessageText) {
      setMessageText(newMessageText);
      setValue("messages", newMessageText, { shouldValidate: true });
    }

    return response;
  };

  const filteredTemplates = MOCK_TEMPLATES.filter((t) =>
    t.name.toLowerCase().includes(templateSearch.toLowerCase())
  );

  const previewText = enableLinkShortening && shortenedUrl && extractedUrl
    ? messageText.replace(extractedUrl, shortenedUrl)
    : messageText;



  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Page Header */}
      <div className="px-4 md:px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Choose Your SMS Template</h1>
          <p className="text-sm text-gray-500 mt-0.5">Pick a message template to edit or create a new one</p>
        </div>
        <button className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors bg-white shadow-sm">
          <Plus className="w-4 h-4" />
          Create New Template
        </button>
      </div>

      {/* Main grid */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">

          {/* ── Left: Template Sidebar ── */}
          <div className="w-full md:w-72 lg:w-80 bg-white border-r border-gray-200 flex flex-col shrink-0">
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Templates"
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className={`w-full text-left px-4 py-3.5 hover:bg-gray-50 transition-colors ${selectedTemplateId === template.id ? "bg-indigo-50 border-r-2 border-indigo-500" : ""
                    }`}
                >
                  <p className={`text-sm font-semibold ${selectedTemplateId === template.id ? "text-indigo-700" : "text-gray-900"}`}>
                    {template.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{template.preview}</p>
                </button>
              ))}
            </div>
          </div>

          {/* ── Center: SMS Composer ── */}
          <form
            id="step-3-form"
            onSubmit={handleSubmit(handleFormSubmit)}
            className="flex-1 overflow-y-auto"
          >
            <div className="p-4 md:p-6 h-full flex flex-col">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex-1 flex flex-col">
                {/* Section header */}
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wide uppercase">SMS Message</h2>
                </div>

                <div className="p-6 flex-1 flex flex-col gap-5">
                  {/* Body label */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Body</label>
                    <Controller
                      name="messages"
                      control={control}
                      rules={{ required: "Message is required" }}
                      render={({ field, fieldState }) => (
                        <div className="relative">
                          <textarea
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              const noEmoji = removeEmojis(e.target.value);
                              field.onChange(noEmoji);
                              setMessageText(noEmoji);
                            }}
                            placeholder="Type your SMS message here..."
                            rows={7}
                            className={`w-full px-4 py-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent ${fieldState.error ? "border-red-300" : "border-gray-200"
                              }`}
                          />
                          {/* Character counter bottom bar */}
                          <div className="flex items-center justify-between mt-1.5 px-1">
                            <span className="text-xs text-gray-400">
                              {totalChars} / {smsMessageUnit * 160} | {smsMessageUnit} SMS
                            </span>
                            <button
                              type="button"
                              onClick={() => setIsConvoOpen(true)}
                              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                              title="Open ConvoAI"
                            >
                              <Link2 className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                          {fieldState.error && (
                            <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Destination URL */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-800">
                        Destination URL{" "}
                        <span className="font-normal text-gray-400">(Optional)</span>
                      </label>
                      {extractedUrl && (
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                          <span>{enableLinkShortening ? "Shorten" : "Shorten URL"}</span>
                          <button
                            type="button"
                            onClick={() => handleToggleShortening(!enableLinkShortening)}
                            className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${enableLinkShortening ? "bg-indigo-500" : "bg-gray-300"
                              }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableLinkShortening ? "translate-x-4" : "translate-x-0"
                                }`}
                            />
                          </button>
                        </label>
                      )}
                    </div>
                    <input
                      type="text"
                      value={
                        enableLinkShortening && shortenedUrl
                          ? shortenedUrl
                          : extractedUrl || ""
                      }
                      placeholder="url"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 bg-gray-50 focus:outline-none"
                    />
                    {loading && (
                      <p className="text-xs text-indigo-500 mt-1">Shortening URL...</p>
                    )}
                    {!isLinkValid && (
                      <p className="text-xs text-red-500 mt-1">Please enter a valid URL</p>
                    )}
                  </div>
                </div>

                {/* Footer actions */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    className="px-5 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Save as Draft
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={onBack}
                      className="px-5 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                    >
                      Schedule and Launch
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {/* ── Right: Toggle Panel (Live Preview / ConvoAI) ── */}
          <div className="hidden lg:flex flex-col w-72 xl:w-80 border-l border-gray-200 bg-white">
            <RightPanel
              messageText={previewText}
              senderName={campaignData?.sender_name}
              isConvoOpen={isConvoOpen}
              onToggleConvo={() => setIsConvoOpen(!isConvoOpen)}
              onConvoSend={handleConvoSend}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsContentCreation;